import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, DescriptionBar, MenuTab, SearchBar, SelectBox, Grid, Modal, ModalInner } from 'components';
import {
  paymentGridColumns,
  paymentStatusMap,
  cancelStatusMap,
  paymentMethodOptions,
  MENU_INDEX,
  getTabStatus,
  getPaymentMethodValue
} from './utils';
import { PaymentItem, PaymentSearchResponse, SearchPaymentByAdminResponse } from './types';
import axios from 'common/AxiosAuth';

const ManagePayment = () => {
  const { t } = useTranslation();
  const [selectedMenuIndex, setSelectedMenuIndex] = useState(MENU_INDEX.PENDING);
  const [searchValue, setSearchValue] = useState<string>('');
  const [paymentMethod, setPaymentMethod] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [paymentData, setPaymentData] = useState<PaymentItem[]>([]);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [totalElements, setTotalElements] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [refundingOrderId, setRefundingOrderId] = useState<string | null>(null);

  // ✅ Payment Status 편집 기능 상태
  const [editingPaymentStatusRowId, setEditingPaymentStatusRowId] = useState<string | null>(null);
  const [paymentStatusEditValue, setPaymentStatusEditValue] = useState<string>('');

  // ✅ 환불 모달 관련 상태 추가
  const [isRefundModalOpened, setIsRefundModalOpened] = useState(false);
  const [refundData, setRefundData] = useState<PaymentItem | null>(null);

  // 백엔드 응답을 프론트 데이터로 변환
  const transformResponseToPaymentItem = (
    response: SearchPaymentByAdminResponse[],
    currentPage: number,
    pageSize: number
  ): PaymentItem[] => {
    return response.map((item, index) => ({
      no: (currentPage - 1) * pageSize + index + 1,
      orderId: item.orderId,
      memberId: item.memberId,
      email: item.email,
      currency: item.currency,
      amount: Number(item.amount),
      createdDate: item.createdDate,
      paymentMethod: item.paymentMethod,
      paymentStatus: item.paymentStatus,
      cancelStatus: item.cancelStatus,
      refund: 'Refund'
    }));
  };

  // ✅ 환불 처리 API 호출
  const refundPayment = async (orderId: string) => {
    try {
      const response = await axios.post('/api/admin/payment/cancel-status', {
        orderId: orderId
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  // ✅ Payment 상태 강제 COMPLETE 변경 API 호출
  const forceCompletePayment = async (orderId: string) => {
    try {
      const response = await axios.patch('/api/admin/payment/force-complete', {
        orderId: orderId
      });
      
      // ✅ COMPLETE 탭으로 자동 이동
      setSelectedMenuIndex(MENU_INDEX.COMPLETE);
      setCurrentPage(1);
      
      return response.data;
    } catch (error: any) {
      console.error('결제 상태 변경 실패:', error);
      let errorMessage = t('managePayment.alerts.statusUpdateFailed');
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }
      alert(errorMessage);
      throw error;
    }
  };

  // 결제 데이터 조회 (실제 API 연동)
  const getPaymentData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      
      // 탭별 paymentStatus 설정
      const status = getTabStatus(selectedMenuIndex);
      params.append('paymentStatus', status);
      
      // 검색어
      if (searchValue.trim()) {
        params.append('keyword', searchValue.trim());
      }
      
      // 결제 방법
      if (paymentMethod > 0) {
        const methodValue = getPaymentMethodValue(paymentMethod);
        params.append('paymentMethod', methodValue);
      }
      
      // 페이징
      params.append('page', (currentPage - 1).toString());
      params.append('size', '10');

      const { data }: { data: PaymentSearchResponse } = await axios.get(
        `/api/admin/payment/search?${params.toString()}`
      );

      if (data.code === 'SUCCESS') {
        // 페이징 정보 설정
        setTotalPages(data.data.totalPages);
        setTotalElements(data.data.totalElements);

        // 데이터 변환
        const transformedData = transformResponseToPaymentItem(
          data.data.content,
          currentPage,
          data.data.size
        );
        
        setPaymentData(transformedData);
      } else {
        console.error('API 에러:', data.message);
        setPaymentData([]);
        setTotalPages(1);
        setTotalElements(0);
      }
    } catch (error) {
      console.error('결제 데이터 조회 실패:', error);
      
      // 에러 시 빈 데이터 설정
      setPaymentData([]);
      setTotalPages(1);
      setTotalElements(0);
      
      // 사용자에게 에러 알림
      alert(t('managePayment.alerts.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  // 컴포넌트 마운트 및 의존성 변경 시 데이터 조회
  useEffect(() => {
    getPaymentData();
  }, [selectedMenuIndex, currentPage]);

  // 검색어 변경 시 currentPage 리셋
  useEffect(() => {
    setCurrentPage(1);
  }, [searchValue, paymentMethod]);

  // 검색어나 결제방법 변경 시 데이터 조회
  useEffect(() => {
    if (currentPage === 1) {
      getPaymentData();
    }
  }, [searchValue, paymentMethod]);

  // 검색 핸들러
  const handleSearch = () => {
    setCurrentPage(1);
    getPaymentData();
  };

  // 리셋 핸들러
  const handleReset = () => {
    setPaymentMethod(0);
    setSearchValue('');
    setCurrentPage(1);
    // 리셋 후 데이터 재조회는 useEffect에서 처리됨
  };

  // 탭 클릭 핸들러
  const onClickTab = (index: number) => {
    setSelectedMenuIndex(index);
    setCurrentPage(1);
  };

  // ✅ Refund 버튼 클릭 핸들러 (모달 열기)
  const handleRefund = async (row: PaymentItem) => {
    setRefundData(row);
    setIsRefundModalOpened(true);
  };

  // ✅ 환불 확인 핸들러 (API 호출)
  const handleRefundConfirmed = async () => {
    if (!refundData) return;
    
    setRefundingOrderId(refundData.orderId);
    try {
      await refundPayment(refundData.orderId);
      
      setIsRefundModalOpened(false);
      setRefundData(null);
      setSelectedMenuIndex(MENU_INDEX.REFUNDED);
      setCurrentPage(1);
      
    } catch (error: any) {
      console.error('환불 처리 실패:', error);
      
      let errorMessage = t('managePayment.alerts.refundFailed');
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }
      
      alert(errorMessage);
    } finally {
      setRefundingOrderId(null);
    }
  };

  // ✅ 환불 모달 닫기 핸들러
  const handleRefundModalClose = () => {
    setIsRefundModalOpened(false);
    setRefundData(null);
  };

  // ✅ Refund 버튼 렌더링 함수
  const renderRefundButton = (row: PaymentItem) => {
    const isRefundable = row.cancelStatus === 'TO_BE_REFUNDED';
    const isAlreadyRefunded = row.cancelStatus === 'REFUNDED';
    const isProcessing = refundingOrderId === row.orderId;

    // 이미 환불 완료된 경우 - 회색 비활성화
    if (isAlreadyRefunded) {
      return (
        <button type="button" className="cursor-not-allowed flex flex-row gap-1 items-center rounded border px-2 h-6 text-xs font-regular bg-gray-200 text-gray-500 border-gray-300" disabled>
          <span className="select-none text-nowrap">{t('managePayment.buttons.refunded')}</span>
        </button>
      );
    }

    // 환불 가능하지 않은 경우 - 회색 비활성화
    if (!isRefundable) {
      return (
        <button type="button" className="cursor-not-allowed flex flex-row gap-1 items-center rounded border px-2 h-6 text-xs font-regular bg-gray-200 text-gray-500 border-gray-300" disabled>
          <span className="select-none text-nowrap">{t('managePayment.buttons.refund')}</span>
        </button>
      );
    }

    // 환불 가능한 경우 - 원래 버튼 색깔
    return (
      <button
        type="button"
        className="cursor-pointer flex flex-row gap-1 items-center rounded border px-2 h-6 text-xs font-regular bg-white text-black border-border_primary hover:bg-blue-50 disabled:opacity-50"
        onClick={() => handleRefund(row)}
        disabled={isProcessing || loading}
      >
        <span className="select-none text-nowrap">
          {isProcessing ? t('managePayment.buttons.processing') : t('managePayment.buttons.refund')}
        </span>
      </button>
    );
  };

  // ✅ Payment Status 렌더링 함수 (편집 기능 포함)
  const renderPaymentStatus = (value: string, row: PaymentItem) => {
    // PENDING 또는 FAILED 탭에서만 편집 가능
    const isEditable = (selectedMenuIndex === MENU_INDEX.PENDING || selectedMenuIndex === MENU_INDEX.FAILED) && (value === 'PENDING' || value === 'FAILED');

    // 현재 편집 중인 행인지 확인
    if (editingPaymentStatusRowId === row.orderId && isEditable) {
      return (
        <SelectBox
          className="min-w-[120px] z-[9999]"
          list={[{ value: 1, label: t('managePayment.buttons.forceComplete') }]} // ✅ number value로 수정
          value={1}
          setValue={() => {}}
          label=""
          onChange={async () => {
            try {
              // API 호출
              await forceCompletePayment(row.orderId);
              
              // 편집 모드 종료
              setEditingPaymentStatusRowId(null);
              setPaymentStatusEditValue('');
            } catch (error) {
              // 에러는 forceCompletePayment에서 처리됨
              setEditingPaymentStatusRowId(null);
              setPaymentStatusEditValue('');
            }
          }}
        />
      );
    }

    // 일반 뱃지 표시
    const status = paymentStatusMap[value] || paymentStatusMap['UNKNOWN'];
    return (
      <div
        title={value}
        onClick={() => {
          if (isEditable) {
            // 편집 모드 시작
            setEditingPaymentStatusRowId(null);
            setPaymentStatusEditValue('');
            
            setTimeout(() => {
              setEditingPaymentStatusRowId(row.orderId);
              setPaymentStatusEditValue(value);
            }, 0);
          }
        }}
        style={{ cursor: isEditable ? 'pointer' : 'default' }}
      >
        <div className={`flex h-6 w-fit items-center rounded-full border px-3 ${status.className}`}>
          <span className="text-nowrap text-small font-regular">{status.title}</span>
        </div>
      </div>
    );
  };

  // * Manage User에서 자동 검색 이동 
  useEffect(() => {
    const autoSearch = sessionStorage.getItem('paymentAutoSearch');
    const searchKeyword = sessionStorage.getItem('paymentSearchKeyword');
    if (autoSearch === 'true' && searchKeyword) {
      setSearchValue(searchKeyword);
      sessionStorage.removeItem('paymentAutoSearch');
      sessionStorage.removeItem('paymentSearchKeyword');
    }
  }, []);

  return (
    <div className="flex h-full w-full flex-col">
      <DescriptionBar
        className="md:px-6"
        title={t('managePayment.pageTitle')}
        description={t('managePayment.description')}
      />

      <div className="w-full flex-1">
        <MenuTab
          menuList={[t('managePayment.tabs.pending'), t('managePayment.tabs.complete'), t('managePayment.tabs.failed'), t('managePayment.tabs.refunded')]}
          selectedMenuIndex={selectedMenuIndex}
          onClickTab={onClickTab}
        />

        <div className="flex flex-1 flex-col px-6 py-6">
          {/* 필터 영역 */}
          <div className="gap-5 p-4 md:p-6 grid grid-cols-2 grid-rows-1 md:grid-cols-3 md:grid-rows-1">
            <SelectBox
              className="max-w-none"
              bgColor="layer2"
              label={t('managePayment.filters.paymentMethod')}
              labelTooltip=""
              list={paymentMethodOptions}
              value={paymentMethod}
              setValue={setPaymentMethod}
            />
            <div className="flex items-end justify-start gap-2 col-span-2">
              <SearchBar
                label={t('managePayment.filters.search')}
                className="flex-1"
                value={searchValue}
                setValue={setSearchValue}
                placeholder={t('managePayment.filters.search')}
              />
              <Button className="h-8" type="blue" text={t('managePayment.buttons.search')} onClick={handleSearch} disabled={loading} />
              <Button className="h-8" type="secondary" text={t('managePayment.buttons.reset')} onClick={handleReset} disabled={loading} />
            </div>
          </div>

          {/* 로딩 상태 표시 */}
          {loading && (
            <div className="flex justify-center items-center py-8">
              <span className="text-gray-500">{t('managePayment.loadingText')}</span>
            </div>
          )}

          {/* 그리드 영역 */}
          <Grid
            gridColumns={paymentGridColumns}
            gridData={paymentData}
            searchValue={searchValue}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            className="mb-4"
            options={{
              useFilter: true,
              useSearch: {
                use: true,
                searchKeys: ['no', 'orderId', 'email', 'createdDate', 'currency', 'amount', 'paymentStatus', 'cancelStatus', 'refund']
              },
              useCheckbox: true,
              useSort: {
                use: true,
                sortKeys: ['createdDate', 'paymentStatus', 'cancelStatus']
              },
              usePagination: {
                use: true,
                maxPage: totalPages
              },
              customCell: {
                amount: (value) => <span>{Number(value).toLocaleString()}</span>,
                paymentStatus: (value, row) => renderPaymentStatus(value, row as PaymentItem),
                cancelStatus: (value) => {
                  const statusValue = value || 'null';
                  const status = cancelStatusMap[statusValue] || cancelStatusMap['UNKNOWN'];
                  return (
                    <div title={value || 'None'}>
                      <div className={`flex h-6 w-fit items-center rounded-full border px-3 ${status.className}`}>
                        <span className="text-nowrap text-small font-regular">{status.title}</span>
                      </div>
                    </div>
                  );
                },
                refund: (value, row) => renderRefundButton(row as PaymentItem)
              }
            }}
          />
        </div>
      </div>
      {/* ✅ Refund 확인 모달 */}
      {isRefundModalOpened && refundData && (
        <Modal isOpened={true}>
          <ModalInner className="md:w-[400px]" title={t('managePayment.refundModal.title')} onClickClose={handleRefundModalClose}>
            <p className="p-4 pt-0 text-body1" style={{ whiteSpace: 'pre-wrap' }}>
              {t('managePayment.refundModal.body', { orderId: refundData.orderId })}
            </p>
            <div className="flex w-full flex-row items-center justify-between bg-bg-transparent_gray_light p-4">
              <Button className="h-9" text={t('managePayment.buttons.close')} onClick={handleRefundModalClose} type="secondary" size="large" />
              <Button className="h-9 bg-red-600 border-red-600" text={t('managePayment.buttons.refund')} onClick={handleRefundConfirmed} size="large" />
            </div>
          </ModalInner>
        </Modal>
      )}
    </div>
  );
};

export default ManagePayment;